import React, { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "../lib/api";
import { PageHeader, Card, Btn, Badge, EmptyState } from "../components/kr";
import { toast } from "sonner";
import { ArrowClockwise, MagnifyingGlass, CaretDown, CaretUp } from "@phosphor-icons/react";
import { Pagination, usePagination } from "../components/Pagination";

const ENTITY_OPTIONS = ["", "documents", "employees", "settings", "users"];
const ACTION_OPTIONS = ["", "create", "update", "delete"];

const actionColor = (a) => {
  if (a === "create") return "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400 border border-emerald-200/50 dark:border-emerald-500/20";
  if (a === "update") return "bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400 border border-amber-200/50 dark:border-amber-500/20";
  if (a === "delete") return "bg-rose-50 text-rose-700 dark:bg-rose-500/10 dark:text-rose-400 border border-rose-200/50 dark:border-rose-500/20";
  return "bg-zinc-50 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300 border border-zinc-200 dark:border-zinc-700";
};

const fmtWhen = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  } catch { return iso; }
};

const StringDiff = ({ before, after }) => {
  if (!before && !after) return null;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-[11px] font-mono tracking-tight mt-1">
      {before && (
        <div className="bg-rose-50/50 dark:bg-rose-950/20 border border-rose-100 dark:border-rose-900/50 rounded-lg overflow-hidden">
          <div className="bg-rose-100/60 dark:bg-rose-950/40 px-3 py-1.5 font-semibold text-rose-800 dark:text-rose-400 border-b border-rose-100 dark:border-rose-900/30 tracking-wide text-[10px] uppercase">
            Before Change
          </div>
          <pre className="p-3 whitespace-pre-wrap break-words text-zinc-600 dark:text-zinc-400 overflow-x-auto leading-relaxed max-h-[300px]">{JSON.stringify(before, null, 2)}</pre>
        </div>
      )}
      {after && (
        <div className="bg-emerald-50/50 dark:bg-emerald-950/20 border border-emerald-100 dark:border-emerald-900/50 rounded-lg overflow-hidden">
          <div className="bg-emerald-100/60 dark:bg-emerald-950/40 px-3 py-1.5 font-semibold text-emerald-800 dark:text-emerald-400 border-b border-emerald-100 dark:border-emerald-900/30 tracking-wide text-[10px] uppercase">
            After Change
          </div>
          <pre className="p-3 whitespace-pre-wrap break-words text-zinc-600 dark:text-zinc-400 overflow-x-auto leading-relaxed max-h-[300px]">{JSON.stringify(after, null, 2)}</pre>
        </div>
      )}
    </div>
  );
};

export default function AuditLog() {
  const [rows, setRows] = useState([]);
  const [entity, setEntity] = useState("");
  const [action, setAction] = useState("");
  const [actor, setActor] = useState("");
  const [expanded, setExpanded] = useState({});
  const [busy, setBusy] = useState(false);

  const { page, setPage, pageSize, setPageSize } = usePagination(10);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = { limit: 200 };
      if (entity) params.entity = entity;
      if (action) params.action = action;
      if (actor) params.actor = actor.trim();
      const r = await api.get("/audit-log", { params });
      setRows(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load audit log");
    } finally { setBusy(false); }
  }, [entity, action, actor]);

  useEffect(() => { load(); }, [load]);
  
  useEffect(() => { setPage(1); }, [entity, action, actor, setPage]);

  const pagedRows = useMemo(() => {
    return rows.slice((page - 1) * pageSize, page * pageSize);
  }, [rows, page, pageSize]);

  return (
    <div className="space-y-6 max-w-[1600px] mx-auto p-1" data-testid="audit-log-page">
      <PageHeader 
        title="Audit Log" 
        subtitle="Comprehensive data trace logs — tracks creation, modifications, and deletions system-wide." 
      />

      <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 shadow-sm rounded-xl p-4 transition-all">
        <div className="flex flex-wrap lg:flex-nowrap gap-4 items-end">
          <div className="w-full sm:w-auto min-w-[140px]">
            <div className="text-[11px] font-medium uppercase tracking-wider text-zinc-400 dark:text-zinc-500 mb-1.5 ml-0.5">Entity</div>
            <select
              value={entity}
              onChange={(e) => setEntity(e.target.value)}
              className="w-full px-3 py-2 bg-zinc-50 dark:bg-zinc-800/50 hover:bg-zinc-100 dark:hover:bg-zinc-800 border border-zinc-200 dark:border-zinc-700/80 text-sm rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all cursor-pointer shadow-sm text-zinc-700 dark:text-zinc-200 font-medium"
              data-testid="audit-filter-entity"
            >
              {ENTITY_OPTIONS.map(e => <option key={e} value={e}>{e || "All Entities"}</option>)}
            </select>
          </div>

          <div className="w-full sm:w-auto min-w-[140px]">
            <div className="text-[11px] font-medium uppercase tracking-wider text-zinc-400 dark:text-zinc-500 mb-1.5 ml-0.5">Action</div>
            <select
              value={action}
              onChange={(e) => setAction(e.target.value)}
              className="w-full px-3 py-2 bg-zinc-50 dark:bg-zinc-800/50 hover:bg-zinc-100 dark:hover:bg-zinc-800 border border-zinc-200 dark:border-zinc-700/80 text-sm rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all cursor-pointer shadow-sm text-zinc-700 dark:text-zinc-200 font-medium"
              data-testid="audit-filter-action"
            >
              {ACTION_OPTIONS.map(a => <option key={a} value={a}>{a || "All Actions"}</option>)}
            </select>
          </div>

          <div className="flex-1 min-w-[240px] w-full">
            <div className="text-[11px] font-medium uppercase tracking-wider text-zinc-400 dark:text-zinc-500 mb-1.5 ml-0.5">Actor Email</div>
            <input
              value={actor}
              onChange={(e) => setActor(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") load(); }}
              placeholder="Filter by actor email..."
              className="w-full px-3 py-2 bg-zinc-50 dark:bg-zinc-800/50 hover:bg-zinc-100 dark:hover:bg-zinc-800 border border-zinc-200 dark:border-zinc-700/80 text-sm rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all shadow-sm text-zinc-700 dark:text-zinc-200 placeholder-zinc-400 dark:placeholder-zinc-500"
              data-testid="audit-filter-actor"
            />
          </div>

          <div className="w-full lg:w-auto">
            <Btn 
              onClick={load} 
              disabled={busy} 
              data-testid="audit-refresh"
              className="w-full lg:w-auto h-[38px] px-4 bg-orange-500 hover:bg-orange-600 active:scale-[0.98] text-white font-medium rounded-lg shadow-sm transition-all flex items-center justify-center gap-2 border-0"
            >
              <ArrowClockwise size={14} className={busy ? "animate-spin" : ""} /> 
              <span>{busy ? "Syncing…" : "Refresh"}</span>
            </Btn>
          </div>
        </div>
      </div>

      {rows.length === 0 ? (
        <EmptyState icon={MagnifyingGlass} title="No audit entries matched" subtitle="Once users execute changes, the historical trace logs will populate here." />
      ) : (
        <>
          <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 shadow-sm rounded-xl overflow-hidden">
            <div className="px-4 py-3 bg-zinc-50/50 dark:bg-zinc-800/30 border-b border-zinc-200 dark:border-zinc-800 flex items-center justify-between">
              <span className="text-xs font-medium text-zinc-500 dark:text-zinc-400 tracking-wide">
                Showing <span className="font-semibold text-zinc-700 dark:text-zinc-200">{rows.length}</span> activity entries · chronological view
              </span>
            </div>

            <div className="divide-y divide-zinc-100 dark:divide-zinc-800/60 p-2 space-y-1.5">
              {pagedRows.map((r) => {
                const key = r.id || `${r.created_at}-${r.entity_id}`;
                const isOpen = !!expanded[key];
                return (
                  <div 
                    key={key} 
                    className={`group border rounded-xl overflow-hidden transition-all duration-200 ${
                      isOpen 
                        ? "border-orange-200 dark:border-orange-900/40 bg-zinc-50/30 dark:bg-zinc-950/20 shadow-sm" 
                        : "border-zinc-100 dark:border-zinc-800/40 hover:border-zinc-200 dark:hover:border-zinc-700 hover:bg-zinc-50/50 dark:hover:bg-zinc-800/20 hover:scale-[1.002]"
                    }`}
                    data-testid={`audit-row-${r.id}`}
                  >
                    <button
                      type="button"
                      onClick={() => setExpanded(x => ({ ...x, [key]: !isOpen }))}
                      className="w-full text-left px-4 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3 cursor-pointer"
                    >
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <span className={`px-2 py-0.5 text-[10px] font-bold tracking-wider rounded-md uppercase shrink-0 select-none ${actionColor(r.action)}`}>
                          {r.action}
                        </span>
                        <div className="shrink-0">
                          <Badge className="bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 font-medium px-2 py-0.5 rounded-md text-[11px] border border-zinc-200 dark:border-zinc-700/50">
                            {r.entity}
                          </Badge>
                        </div>
                        <span className="text-sm font-medium text-zinc-700 dark:text-zinc-200 truncate group-hover:text-zinc-900 dark:group-hover:text-white transition-colors">
                          {r.summary || "—"}
                        </span>
                      </div>

                      <div className="flex items-center justify-between sm:justify-end gap-4 shrink-0 border-t sm:border-0 pt-2 sm:pt-0 border-zinc-100 dark:border-zinc-800">
                        <span className="text-xs text-zinc-400 dark:text-zinc-500 font-mono truncate max-w-[180px] sm:max-w-none">
                          {r.actor_email || "system"}
                        </span>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="text-xs text-zinc-400 dark:text-zinc-500 font-mono whitespace-nowrap bg-zinc-50 dark:bg-zinc-800/40 px-2 py-0.5 rounded border border-zinc-200/40 dark:border-zinc-700/30">
                            {fmtWhen(r.created_at)}
                          </span>
                          <div className="text-zinc-400 dark:text-zinc-500 group-hover:text-zinc-600 dark:group-hover:text-zinc-300 transition-colors hidden sm:block">
                            {isOpen ? <CaretUp size={14} weight="bold" /> : <CaretDown size={14} weight="bold" />}
                          </div>
                        </div>
                      </div>
                    </button>
                    
                    {isOpen && (
                      <div className="px-4 pb-4 pt-1 border-t border-zinc-200/60 dark:border-zinc-800/60 bg-zinc-50/70 dark:bg-zinc-950/40 space-y-3">
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] font-medium text-zinc-400 dark:text-zinc-500 select-none bg-white dark:bg-zinc-900 p-2 rounded-lg border border-zinc-200/50 dark:border-zinc-800/50 shadow-2xs w-fit">
                          <div>
                            ID: <span className="font-mono text-zinc-600 dark:text-zinc-300 font-semibold">{r.entity_id || "—"}</span>
                          </div>
                          <div className="w-1 h-1 bg-zinc-300 dark:bg-zinc-700 rounded-full hidden sm:block" />
                          <div>
                            Role: <span className="font-mono text-zinc-600 dark:text-zinc-300 font-semibold">{r.actor_role || "—"}</span>
                          </div>
                        </div>
                        <StringDiff before={r.before} after={r.after} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <Pagination
            page={page}
            pageSize={pageSize}
            total={rows.length}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            testId="audit-pager"
            className="border-0 bg-transparent px-0 mt-4"
          />
        </>
      )}
    </div>
  );
}